/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  DateFormats,
  formatISODate,
  Icon,
  Tooltip,
  Typography,
} from '@omnipresentgroup/design-system';
import type { TimeOff, TimeOffCompany } from '@omnipresentgroup/types';
import {
  type ColumnDef,
  createColumnHelper,
  sortingFns,
} from '@tanstack/react-table';
import { useMemo } from 'react';

import { TimeOffLeaveTypeTag } from '../../time-off-leave-type-tag';
import { TimeOffStatusTag } from '../../time-off-status-tag';

const FIRST_DAY_AFTERNOON_ONLY_TOOLTIP =
  'Your time off starts in the afternoon';
const LAST_DAY_MORNING_ONLY_TOOLTIP = 'Your time off only covers the morning';

export enum ColumnHeaders {
  id = 'id',
  employeeName = 'Name',
  startDate = 'Start date',
  endDate = 'End date',
  leaveType = 'Leave type',
  employeeNote = 'Employee note',
  status = 'Status',
  actions = '',
  firstDayAfternoonOnly = '',
  lastDayMorningOnly = '',
}

export const useTimeOffsTableConfig = ({
  columnsOverride,
}: {
  columnsOverride?: ColumnDef<TimeOff | TimeOffCompany, unknown>[];
}) => {
  const columnHelper = useMemo(
    () => createColumnHelper<TimeOff | TimeOffCompany>(),
    [],
  );

  const columns: ColumnDef<TimeOff | TimeOffCompany, any>[] = useMemo(
    () => [
      columnHelper.accessor('employeeName', {
        cell: (info) => (info.row.original as TimeOffCompany).employeeName,
        header: () => ColumnHeaders.employeeName,
      }),
      columnHelper.accessor('startDate', {
        cell: (info) => (
          <Typography size="14" as="span">
            {formatISODate(
              info.row.original.startDate,
              DateFormats.WeekDate,
              true,
              '-',
            )}
          </Typography>
        ),
        header: () => ColumnHeaders.startDate,
        sortingFn: sortingFns.datetime,
      }),
      columnHelper.display({
        id: 'firstDayAfternoonOnly',
        header: ColumnHeaders.firstDayAfternoonOnly,
        cell: (info) =>
          info.row.original.firstDayAfternoonOnly ? (
            <Tooltip
              id={`tooltip-startdate-${info.row.original.id}`}
              description={FIRST_DAY_AFTERNOON_ONLY_TOOLTIP}
            >
              <Icon size="16" icon="Sunset" title="" />
            </Tooltip>
          ) : (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <></>
          ),
        size: 10,
      }),
      columnHelper.accessor('endDate', {
        cell: (info) => (
          <Typography size="14" as="span">
            {formatISODate(
              info.row.original.endDate,
              DateFormats.WeekDate,
              true,
              '-',
            )}
          </Typography>
        ),
        header: () => ColumnHeaders.endDate,
        sortingFn: sortingFns.datetime,
      }),
      columnHelper.display({
        id: 'lastDayMorningOnly',
        header: ColumnHeaders.lastDayMorningOnly,
        cell: (info) =>
          info.row.original.lastDayMorningOnly ? (
            <Tooltip
              id={`tooltip-endate-${info.row.original.id}`}
              description={LAST_DAY_MORNING_ONLY_TOOLTIP}
            >
              <Icon size="16" icon="Sunrise" title="" />
            </Tooltip>
          ) : (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <></>
          ),
        size: 10,
      }),
      columnHelper.accessor('leaveType', {
        cell: (info) => <TimeOffLeaveTypeTag leaveType={info.getValue()} />,
        header: () => ColumnHeaders.leaveType,
      }),
      columnHelper.accessor('employeeNote', {
        cell: (info) => info.row.original.employeeNote,
        header: () => ColumnHeaders.employeeNote,
      }),
      columnHelper.accessor('status', {
        cell: (info) => <TimeOffStatusTag timeOff={info.row.original} />,
        header: () => ColumnHeaders.status,
      }),
      ...(columnsOverride ?? []),
    ],
    [columnHelper, columnsOverride],
  );

  return {
    columns,
    columnHelper,
  };
};
